import { useEffect, useState } from 'react';
import { resolvePlacePhotoUrl } from '../utils/photo-cache.utils';

interface UsePlacePhotoUrlsResult {
  urls: string[];
  isLoading: boolean;
}

/**
 * Resolves photoReferences to renderable image URLs via the getPlacePhoto
 * gateway (cached per session, so repeat views don't re-invoke the function).
 * Cards written before the imgUrls -> photoReferences migration (SEC-04) only
 * have legacy `imgUrls` — already-resolved, key-bearing URLs — and no
 * photoReferences yet; those render as-is until MIG-05 backfills them. This
 * hook never writes to Firestore itself. Pass `enabled = false` to defer
 * resolving until the photos are actually needed.
 */
export function usePlacePhotoUrls(
  photoReferences: string[] | undefined,
  legacyImgUrls: string[] | undefined,
  enabled = true
): UsePlacePhotoUrlsResult {
  const hasReferences = Boolean(photoReferences?.length);
  const [urls, setUrls] = useState<string[]>(hasReferences ? [] : (legacyImgUrls ?? []));
  const [isLoading, setIsLoading] = useState(hasReferences);
  const referencesKey = photoReferences?.join(',') ?? '';

  // referencesKey captures content changes to photoReferences by value;
  // legacyImgUrls is only read in the empty-references branch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: referencesKey stands in for photoReferences
  useEffect(() => {
    if (!photoReferences?.length) {
      setUrls(legacyImgUrls ?? []);
      setIsLoading(false);
      return;
    }

    // Every resolve is a gateway call, and the gateway is rate-limited per user,
    // so off-screen cards and closed modals must not resolve anything.
    if (!enabled) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    Promise.allSettled(photoReferences.map((ref) => resolvePlacePhotoUrl(ref))).then((settled) => {
      if (cancelled) {
        return;
      }
      if (process.env.NODE_ENV === 'development') {
        settled.forEach((result) => {
          if (result.status === 'rejected') {
            // biome-ignore lint/suspicious/noConsole: dev-only hint; failures are otherwise silent
            console.warn(
              'getPlacePhoto failed — is the functions emulator running on :5001?',
              result.reason
            );
          }
        });
      }
      const resolved = settled
        .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
        .map((result) => result.value);
      setUrls(resolved);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [referencesKey, enabled]);

  return { urls, isLoading };
}
